import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  Plus, 
  List, 
  Package, 
  Users, 
  ShoppingCart,
  TrendingUp,
  Clock,
  CheckCircle,
  UserCog
} from 'lucide-react';

function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    ordiniOggi: 0,
    ordiniSettimana: 0,
    prodottiTotali: 0,
    fornitoriAttivi: 0,
    utentiTotali: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const promises = [
        axios.get('/api/ordini', { headers }),
        axios.get('/api/prodotti', { headers }),
        axios.get('/api/fornitori', { headers })
      ];

      // Aggiungi la chiamata utenti solo se l'utente è admin
      if (user?.ruolo === 'admin') {
        promises.push(axios.get('/api/utenti', { headers }));
      }

      const responses = await Promise.all(promises);
      const [ordiniRes, prodottiRes, fornitoriRes, utentiRes] = responses;

      const oggi = new Date().toISOString().split('T')[0];
      const ordiniOggi = ordiniRes.data.data.filter(ordine => ordine.data_ordine === oggi).length;
      
      const unaSettimanaFa = new Date();
      unaSettimanaFa.setDate(unaSettimanaFa.getDate() - 7);
      const ordiniSettimana = ordiniRes.data.data.filter(ordine => 
        new Date(ordine.data_ordine) >= unaSettimanaFa
      ).length;

      setStats({
        ordiniOggi,
        ordiniSettimana,
        prodottiTotali: prodottiRes.data.data.length,
        fornitoriAttivi: fornitoriRes.data.data.length,
        utentiTotali: utentiRes ? utentiRes.data.data.length : 0
      });
    } catch (error) {
      console.error('Errore caricamento statistiche:', error);
    } finally {
      setLoading(false);
    }
  };

  const baseQuickActions = [
    {
      name: 'Nuovo Ordine',
      description: 'Crea un nuovo ordine fornitori',
      href: '/nuovo-ordine',
      icon: Plus,
      color: 'bg-blue-500 hover:bg-blue-600'
    },
    {
      name: 'Visualizza Ordini',
      description: 'Gestisci gli ordini esistenti',
      href: '/ordini',
      icon: List,
      color: 'bg-green-500 hover:bg-green-600'
    },
    {
      name: 'Gestisci Prodotti',
      description: 'Aggiungi o modifica prodotti',
      href: '/prodotti',
      icon: Package,
      color: 'bg-purple-500 hover:bg-purple-600'
    }
  ];

  // Menu completo per admin
  const quickActions = user?.ruolo === 'admin'
    ? [
        ...baseQuickActions,
        {
          name: 'Gestisci Fornitori',
          description: 'Configura i fornitori',
          href: '/fornitori',
          icon: Users,
          color: 'bg-orange-500 hover:bg-orange-600'
        },
        {
          name: 'Gestisci Utenti',
          description: 'Crea e gestisci gli utenti',
          href: '/utenti',
          icon: UserCog,
          color: 'bg-red-500 hover:bg-red-600'
        }
      ]
    : baseQuickActions;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Panoramica del sistema di gestione ordini
        </p>
      </div>

      {/* Azioni Rapide - subito sopra, sempre a 2 colonne (anche su mobile) */}
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-4">Azioni Rapide</h2>
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <Link
                key={action.name}
                to={action.href}
                className="relative group bg-white p-4 sm:p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-500 rounded-lg shadow hover:shadow-md transition-shadow"
              >
                <div>
                  <span className={`rounded-lg inline-flex p-2 sm:p-3 ${action.color} text-white`}>
                    <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
                  </span>
                </div>
                <div className="mt-2 sm:mt-4">
                  <h3 className="text-sm sm:text-lg font-medium text-gray-900">
                    {action.name}
                  </h3>
                  <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-gray-500 line-clamp-2">
                    {action.description}
                  </p>
                </div>
                <span
                  className="absolute -top-6 -right-6 bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-hidden="true"
                >
                  →
                </span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Statistiche - sotto, sempre a 2 colonne (anche su mobile) */}
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-4">Statistiche</h2>
        <div className="grid grid-cols-2 gap-3 sm:gap-5">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-3 sm:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Ordini Oggi
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {stats.ordiniOggi}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-3 sm:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Ordini Settimana
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {stats.ordiniSettimana}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-3 sm:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Package className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Prodotti Totali
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {stats.prodottiTotali}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-3 sm:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-5 w-5 sm:h-6 sm:w-6 text-orange-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Fornitori Attivi
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {stats.fornitoriAttivi}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {user?.ruolo === 'admin' && (
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-3 sm:p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <UserCog className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Utenti Sistema
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {stats.utentiTotali}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Attività Recente
          </h3>
          <div className="mt-5">
            <div className="flow-root">
              <ul className="-mb-8">
                <li>
                  <div className="relative pb-8">
                    <div className="relative flex space-x-3">
                      <div>
                        <span className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center ring-8 ring-white">
                          <CheckCircle className="h-5 w-5 text-white" />
                        </span>
                      </div>
                      <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                        <div>
                          <p className="text-sm text-gray-500">
                            Sistema inizializzato correttamente
                          </p>
                        </div>
                        <div className="text-right text-sm whitespace-nowrap text-gray-500">
                          <Clock className="inline h-4 w-4 mr-1" />
                          Ora
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;

